import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { isAnchorAllowedChild } from '@bitComponents/utils/accessibility';
import { sendCMSEvent, checkEventObj } from '@bitComponents/utils/metrics';
import { giveRandomNumber } from '@bitComponents/utils/common';
import { CUSTOM_LINK_CONTENT } from '@bitComponents/utils/tagging';
import { errorLog, warnLog } from '@bitComponents/utils/logUtils';
import './CustomLink.scss';

const ANALYTICS_PARAMS = {
  posCode: 'prevPosCode',
  phCode: 'prevPhCode',
  webCode: 'prevWebCode',
};

const COMPONENT_NAME = 'CustomLink';

const addParamsToLink = (link, taggingInfo) => {
  if (link.indexOf('tel:') === 0) {
    return link;
  }

  let result = link;
  let separator = link.indexOf('?') < 0 ? '?' : '&';
  Object.keys(ANALYTICS_PARAMS).forEach((key) => {
    if (
      taggingInfo &&
      taggingInfo.componentPerso &&
      taggingInfo.componentPerso[key]
    ) {
      result += `${separator}${ANALYTICS_PARAMS[key]}=${taggingInfo.componentPerso[key]}`;
      separator = '&';
    }
  });
  return result;
};
const isExternalLink = (link = '') => {
  let external = false;
  if (
    link &&
    (link.indexOf('movistar.es') > -1 ||
      link.indexOf('col10mgmt.tsm.inet') > -1 ||
      link.indexOf('http:') > -1 ||
      link.indexOf('https:') > -1 ||
      link.indexOf('www.') > -1 ||
      link.indexOf('data:application/') > -1)
  ) {
    external = true;
  }
  return external;
};

const normalizeExternalLink = (link) => {
  let normalizedLink = link;
  if (link.startsWith('www.')) {
    normalizedLink = `http://${link}`;
  }
  return normalizedLink;
};

const CustomLink = ({
  id,
  onClick,
  link,
  taggingInfo,
  buttonType,
  text,
  accInfo,
  className,
  target,
  isBlockElement,
  children,
  forceExternal,
  download,
  disable,
  cySelector,
}) => {
  // checking id
  let finalDOMId = id;
  if (!finalDOMId) {
    finalDOMId = `CustomLink-${giveRandomNumber(1, 999)}`;
  }
  //
  let insideInfo = '';
  const production = process.env.NODE_ENV === 'production';
  let isValid = true;

  const childrenArray = React.Children.toArray(children);
  if (childrenArray.length !== 0) {
    if (!production) {
      childrenArray.forEach((element) => {
        if (element && element.type && element.type.name) {
          if (!isAnchorAllowedChild(element.type.name)) {
            isValid = false;
          }
        } else if (element && element.type) {
          if (!isAnchorAllowedChild(element.type)) {
            isValid = false;
          }
        }
      });
    }

    if (isValid) {
      insideInfo = children;
    } else {
      errorLog(
        '[CustomLink]: children must be inline elements',
        '[CustomLink]: inlinechildren',
      );
      return null;
    }
  } else {
    insideInfo = text;
  }
  /* Checkings */
  if (isBlockElement) {
    warnLog(
      '[CustomLink]: Make sure that my parent node has position relative',
      'CustomLink-isBlockElement',
    );
  }
  if (!onClick && !link && buttonType !== 'submit') {
    errorLog(
      '[CustomLink]: Doesnt make sense that CustomLink has not link neither onClick',
      'CustomLink-hasLink|onClick',
    );
    return null;
  }

  const externalLink = isExternalLink(link) || forceExternal;

  if (target && target !== '_self') {
    warnLog(
      '[CustomLink]: Becareful working with target prop',
      'CustomLink-target',
    );
  }
  if (!taggingInfo) {
    errorLog(
      '[CustomLink]: taggingInfo is not present',
      '[CustomLink]:taggingInfo',
    );
    return null;
  }

  const taggingWellFormed = checkEventObj(taggingInfo);
  if (!taggingWellFormed) {
    errorLog(
      '[CustomLink]: taggingInfo does not satisfy conditions',
      '[CustomLink]:taggingInfo shape',
    );
    return null;
  }
  /* CHEEKING ENDS */
  /* Desencapsulating accesibilityInfo */
  const { hideAccesible, title, ariaLabel, screenReadable } = accInfo;
  const isHideAccesibilityCss = hideAccesible ? ' hideacc' : '';
  const classNameCss = className ? ` ${className}` : '';
  const isBlockCss = isBlockElement ? ' linkoverbox' : '';
  /* Handling OnClick */
  const handleClick = () => {
    if (!disable) {
      const taggingInfoCompleted = { eventType: 'click', ...taggingInfo };
      if (!taggingInfoCompleted.componentName) {
        taggingInfoCompleted.componentName = COMPONENT_NAME;
      }
      if (!taggingInfoCompleted.componentContent) {
        taggingInfoCompleted.componentContent = CUSTOM_LINK_CONTENT.DEFAULT;
      }
      sendCMSEvent(taggingInfoCompleted);
      if (onClick) {
        onClick();
        return true;
      }
    }
    return false;
  };
  /* Handling onKeyDown */
  const handleOnKeyDown = (event) => {
    const e = event || evt; // for trans-browser compatibility
    const charCode = e.which || e.keyCode;
    if (charCode && charCode === 13) {
      handleClick();
    }
    return false;
  };
  const handleMouseDown = (event) => {
    const taggingInfoCompleted = { eventType: 'click', ...taggingInfo };
    const e = event || evt;
    if (e.button === 1) {
      sendCMSEvent(taggingInfoCompleted);
      handleClick();
    }
  };

  /**
   * obtiene la clase css cuando está en modo deshabilitado
   * @param {boolean} disable flag deshabilitado
   * @returns {string}
   */
  const getDisableClass = (disable) => {
    let classCss = '';
    if (disable) {
      classCss = 'disable';
    }
    return classCss;
  };
  /* LINK */
  if (link) {
    const href = !disable ? addParamsToLink(link, taggingInfo) : '#';
    // EXTERNAL
    if (externalLink) {
      const normalizedExternalLink = normalizeExternalLink(href);
      return (
        <a
          id={finalDOMId}
          aria-label={ariaLabel}
          aria-hidden={!screenReadable}
          title={title}
          className={`CustomLink CustomLinkR1${isBlockCss}${isHideAccesibilityCss}${classNameCss} ${getDisableClass(
            disable,
          )}`}
          href={normalizedExternalLink}
          target={target}
          rel="noopener noreferrer"
          tabIndex="0"
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onKeyDown={handleOnKeyDown}
          download={download || false}
          cy-data={cySelector}
        >
          {insideInfo}
        </a>
      );
    }
    // INTERNAL
    return (
      <Link
        id={finalDOMId}
        className={`CustomLink CustomLinkR1${isBlockCss}${isHideAccesibilityCss}${classNameCss} ${getDisableClass(
          disable,
        )}`}
        to={href}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onKeyDown={handleOnKeyDown}
        cy-data={cySelector}
      >
        {insideInfo}
      </Link>
    );
  }
  if(buttonType === 'link'){
    return (
      <a
        id={finalDOMId}
        aria-label={ariaLabel}
        aria-hidden={!screenReadable}
        title={title}
        className={`CustomLink CustomLinkR1${isBlockCss}${isHideAccesibilityCss}${classNameCss} ${getDisableClass(
          disable,
        )}`}
        tabIndex="0"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onKeyDown={handleOnKeyDown}
        cy-data={cySelector}
      >
        {insideInfo}
      </a>
    );
  }
  /* NO LINK so it should have onClick */
  return (
    <button
      id={finalDOMId}
      type={buttonType}
      aria-label={ariaLabel}
      aria-hidden={!screenReadable}
      title={title}
      className={`CustomLink CustomLinkR1${isBlockCss}${isHideAccesibilityCss}${classNameCss} ${getDisableClass(
        disable,
      )}`}
      tabIndex="0"
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onKeyDown={handleOnKeyDown}
      cy-data={cySelector}
    >
      {insideInfo}
    </button>
  );
};

CustomLink.propTypes = {
  /**
   * {
   *  "info": "Establece un id para el elemento DOM",
   *  "kind": "playground",
   *  "beautifulName": "id"
   * }
   */
  id: PropTypes.string,
  /**
   * {
   *  "info": "Url asociada al componente",
   *  "kind": "both",
   *  "beautifulName": "Enlace"
   * }
   */
  link: PropTypes.string,
  /**
   * {
   *  "info": "Texto que aparecerá dentro del componente",
   *  "kind": "both",
   *  "beautifulName": "Texto"
   * }
   */
  text: PropTypes.string,
  /**
   * {
   *  "info": "Función js que se ejecutará al hacer click, si es necesario",
   *  "kind": "both",
   *  "beautifulName": "Funcion"
   * }
   */
  onClick: PropTypes.func,
  /**
   * {
   *  "info": "Define la información para la accesibilidad del componente como: title, ariaLabel, screenReadable, hideAccesible",
   *  "kind": "playground",
   *  "beautifulName": "Accesibilidad"
   * }
   */
  accInfo: PropTypes.object,
  /**
   * {
   *  "info": "Establece si el elemento debe comportarse como un bloque o no",
   *  "kind": "both",
   *  "beautifulName": "Elemento bloque"
   * }
   */
  isBlockElement: PropTypes.bool,
  /**
   * {
   *  "info": "Establece el nombre al fichero recibido como link en caso de que se vaya a descargar",
   *  "kind": "both",
   *  "beautifulName": "Descargar"
   * }
   */
  download: PropTypes.string,
  /**
   * {
   *  "info": "Permite contribuir la infomación que será enviada a analítica tras la interacción (objeto para el taggeado)",
   *  "kind": "both",
   *  "beautifulName": "Propiedades tagueo"
   * }
   */
  taggingInfo: PropTypes.object.isRequired,
  /**
   * {
   *  "info": "Define si el enlace se tiene que forzar a comportar como enlace externo",
   *  "kind": "both",
   *  "beautifulName": "Enlace externo"
   * }
   */
  forceExternal: PropTypes.bool,
  /**
   * {
   *  "info": "Define donde tiene tiene que abrirse el enlace asociado al componente",
   *  "kind": "both",
   *  "beautifulName": "Target"
   * }
   */
  target: PropTypes.oneOf(['_self', '_blank', 'parent', '_top']),
  /**
   * {
   *  "info": "Define el tipo de boton que renderiza el customLink",
   *  "kind": "both",
   *  "beautifulName": "Target"
   * }
   */
  buttonType: PropTypes.oneOf(['button', 'submit', 'reset', 'link']),
  /**
   * {
   *  "info": "Define si el boton esta inactiva",
   *  "kind": "both",
   *  "beautifulName": "Target"
   * }
   */
  disable: PropTypes.bool,
  cySelector: PropTypes.string,
};

CustomLink.defaultProps = {
  id: '',
  onClick: null,
  link: null,
  text: '',
  accInfo: {
    title: '',
    ariaLabel: '',
    // its apply aria-hidden
    screenReadable: true, // true => !aria-hidden
    hideAccesible: false, // className=hideaccesibility
  },
  isBlockElement: false,
  download: '',
  forceExternal: false,
  target: '_self',
  buttonType: 'button',
  disable: false,
  cySelector: '',
};

export default CustomLink;
